import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, Plus } from 'lucide-react';

const MAX_SHOWN = 60;
const natural = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

// Inline search on jewellery numbers with a multi-select dropdown; onAdd(pieces[]).
export function JewelleryMultiPicker({ items = [], excludeIds = [], onAdd, testid = 'jewellery-multi-picker', placeholder = 'Type jewellery no.', up = false, note }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const ref = useRef(null);
  const taken = useMemo(() => new Set(excludeIds), [excludeIds]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = items.filter((it) => !taken.has(it.id) && (!s || String(it.jewellery_no || '').toLowerCase().includes(s)));
    return list.sort((a, b) => {
      const as = String(a.jewellery_no || '').toLowerCase().startsWith(s); const bs = String(b.jewellery_no || '').toLowerCase().startsWith(s);
      return as !== bs ? (as ? -1 : 1) : natural(a.jewellery_no, b.jewellery_no);
    });
  }, [items, taken, q]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const isSel = (id) => sel.some((x) => x.id === id);
  const toggle = (it) => setSel((s) => (isSel(it.id) ? s.filter((x) => x.id !== it.id) : [...s, it]));
  const commit = (list) => { if (!list.length) return; onAdd(list); setSel([]); setQ(''); setOpen(false); };
  const onKey = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (sel.length) { commit(sel); return; }
    const s = q.trim().toLowerCase();
    const exact = matches.find((it) => String(it.jewellery_no || '').toLowerCase() === s);
    if (exact || matches.length === 1) commit([exact || matches[0]]);
  };
  const shown = matches.slice(0, MAX_SHOWN);
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey} placeholder={placeholder}
            className="h-10 w-full rounded-lg border border-input bg-background pl-8 pr-3 font-mono text-sm" data-testid={`${testid}-input`} />
        </div>
        <button type="button" disabled={!sel.length} onClick={() => commit(sel)} className="inline-flex h-10 shrink-0 items-center gap-1 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-40" data-testid={`${testid}-add-button`}>
          <Plus size={15} /> Add{sel.length ? ` ${sel.length}` : ''}
        </button>
      </div>
      {open ? (
        <div className={`absolute left-0 right-0 z-[320] max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-xl ${up ? 'bottom-full mb-1' : 'mt-1'}`} data-testid={`${testid}-dropdown`}>
          {shown.length ? (
            <>
              <div className="sticky top-0 flex items-center justify-between border-b border-border bg-card px-3 py-1.5 text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">
                <span>{matches.length} match{matches.length === 1 ? '' : 'es'}{matches.length > MAX_SHOWN ? ` · first ${MAX_SHOWN}` : ''}{sel.length ? ` · ${sel.length} selected` : ''}</span>
                <button type="button" onClick={() => setSel((s) => [...s, ...shown.filter((it) => !s.some((x) => x.id === it.id))])} className="font-medium text-[hsl(var(--ring))]" data-testid={`${testid}-select-all`}>Select all shown</button>
              </div>
              {shown.map((it) => (
                <button type="button" key={it.id} onClick={() => toggle(it)} className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary ${isSel(it.id) ? 'bg-[hsl(var(--accent))]' : ''}`} data-testid={`${testid}-option`}>
                  <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${isSel(it.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-input'}`}>{isSel(it.id) ? <Check size={11} /> : null}</span>
                  <span className="font-mono font-semibold">{it.jewellery_no}</span>
                  <span className="truncate text-xs text-muted-foreground">{[it.jewellery_type, it.title].filter(Boolean).join(' · ')}</span>
                  {note ? <span className="ml-auto shrink-0 whitespace-nowrap text-[length:calc(var(--fs,1)*0.6875rem)] font-medium text-destructive" data-testid={`${testid}-option-note`}>{note(it)}</span> : null}
                </button>
              ))}
            </>
          ) : <div className="px-3 py-3 text-center text-xs text-muted-foreground" data-testid={`${testid}-empty`}>No matching pieces.</div>}
        </div>
      ) : null}
    </div>
  );
}
